import { CompanyResponseDto, VesselDto } from '@planning/app/api'
import {
  FieldStore,
  FormStore,
  ListFieldStore,
  required,
  requiredArray,
  validateSubForm,
} from '@tom-ui/utils'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

export type VesselVisitGeneralInformationFormStoreType = {
  portCallId?: string | null
  vesselServiceId?: number | null
  vesselIds?: number[]
  vessel?: VesselDto
  vesselOperator?: CompanyResponseDto | null
  shippingAgent?: CompanyResponseDto | null
  portRotation?: Array<string> | null
  vesselNotes?: string
  voyages?: VoyageDto[]
  visitNumber?: string | null
}

export type VoyageDto = {
  id?: number | null
  inboundNumber?: string | null
  outboundNumber?: string | null
  shippingLine?: CompanyResponseDto | null
}

export type VoyageType = {
  id?: number | null
  inboundNumber: FieldStore<string>
  outboundNumber: FieldStore<string>
  shippingLine: FieldStore<CompanyResponseDto | null | undefined>
}

const createVoyageItem = (value?: VoyageDto): VoyageType => {
  const newForm = {
    id: value?.id,
    inboundNumber: new FieldStore(value?.inboundNumber ?? '', required()),
    outboundNumber: new FieldStore(value?.outboundNumber ?? '', required()),
    shippingLine: new FieldStore<CompanyResponseDto | null | undefined>(value?.shippingLine),
  }

  newForm.shippingLine.touch()

  return newForm
}

export class VesselVisitGeneralInformationFormStore extends FormStore<VesselVisitGeneralInformationFormStoreType> {
  vesselIds: FieldStore<number[]>
  vesselOperator: FieldStore<CompanyResponseDto | null | undefined>
  shippingAgent: FieldStore<CompanyResponseDto | null | undefined>
  portCallId: FieldStore<string | null | undefined>
  vesselServiceId: FieldStore<number | null>
  portRotation: FieldStore<Array<string> | null | undefined>
  vesselNotes: FieldStore<string>
  voyages = new ListFieldStore<VoyageType>([createVoyageItem()], (voyages: VoyageType[]) => {
    const touchedVoyages = voyages.filter(
      voyage =>
        voyage.inboundNumber.isTouched ||
        voyage.outboundNumber.isTouched ||
        voyage.shippingLine.isTouched,
    )

    let hasError = false

    for (let index = 0; index < touchedVoyages.length; index++) {
      const voyage = touchedVoyages[index]

      if (voyages.length > 1 && !voyage.shippingLine.value) {
        voyage.shippingLine.setError('fieldIsRequired')
        hasError = true
      } else if (
        voyages.length > 1 &&
        touchedVoyages.find(
          (v, i) => i !== index && v.shippingLine.value?.id === voyage.shippingLine.value?.id,
        )
      ) {
        voyage.shippingLine.setError('fieldMustBeUnique')
        hasError = true
      }
    }

    return hasError ? 'fieldIsRequired' : undefined
  })

  visitNumber: FieldStore<string>

  selectedVessel?: VesselDto

  constructor(initialValues?: Partial<VesselVisitGeneralInformationFormStoreType>) {
    super(initialValues)

    makeObservable(this, {
      voyages: observable,
      selectedVessel: observable,

      selectedVesselNames: computed,

      setData: action,
      addVoyage: action,
      removeVoyage: action,
      clearVesselData: action,
      reset: action,
      patchData: action,
      setSelectedVessel: action,
      updateInitialValueWithCurrentData: action,
    })

    this.vesselIds = new FieldStore<number[]>(initialValues?.vesselIds ?? [], requiredArray)
    this.vesselOperator = new FieldStore<CompanyResponseDto | null | undefined>(
      initialValues?.vesselOperator,
      required(),
    )
    this.shippingAgent = new FieldStore<CompanyResponseDto | null | undefined>(
      initialValues?.shippingAgent,
      required(),
    )
    this.portCallId = new FieldStore(initialValues?.portCallId)
    this.vesselServiceId = new FieldStore<number | null>(
      initialValues?.vesselServiceId ?? null,
      required(),
    )
    this.portRotation = new FieldStore<Array<string> | null | undefined>(
      initialValues?.portRotation,
      val => requiredArray(val),
    )
    this.vesselNotes = new FieldStore<string>(initialValues?.vesselNotes ?? '')
    this.visitNumber = new FieldStore<string>(initialValues?.visitNumber ?? '')

    runInAction(() => {
      if (initialValues?.voyages) {
        this.voyages.setValue([])
        initialValues?.voyages?.forEach(voyage => {
          this.voyages.push(createVoyageItem(voyage))
        })
      }
    })

    this.setSelectedVessel(initialValues?.vessel)
  }

  // todo: add unit test
  setData(data: Partial<VesselVisitGeneralInformationFormStoreType>) {
    this.portCallId.setValue(data.portCallId)
    this.vesselIds.setValue(data.vesselIds ?? [])
    this.vesselOperator.setValue(data.vesselOperator)
    this.shippingAgent.setValue(data.shippingAgent ?? undefined)
    this.vesselServiceId.setValue(data.vesselServiceId ?? null)
    this.portRotation.setValue(data.portRotation ?? undefined)

    this.voyages.setValue([])
    data.voyages?.forEach(voyage => this.addVoyage(voyage))
  }

  patchData(data: Partial<VesselVisitGeneralInformationFormStoreType>) {
    if (data.portCallId) {
      this.portCallId.setValue(data.portCallId)
    }
  }

  addVoyage = (value?: VoyageDto) => {
    this.voyages.push(createVoyageItem(value))
  }

  removeVoyage = (index: number) => {
    this.voyages.removeByIndex(index)
  }

  setSelectedVessel(vessel?: VesselDto) {
    this.selectedVessel = vessel
  }

  get data(): VesselVisitGeneralInformationFormStoreType {
    return {
      portCallId: this.portCallId.value,
      portRotation: this.portRotation.value,
      vesselIds: this.vesselIds.value,
      vesselOperator: this.vesselOperator.value,
      shippingAgent: this.shippingAgent.value,
      vesselServiceId: this.vesselServiceId.value,
      voyages: this.voyages.value.map(voyage => ({
        id: voyage.id,
        inboundNumber: voyage.inboundNumber.value,
        outboundNumber: voyage.outboundNumber.value,
        shippingLine: voyage.shippingLine.value,
      })),
      vessel: this.selectedVessel,
      visitNumber: this.visitNumber.value,
      vesselNotes: this.vesselNotes.value,
    }
  }

  get isDirty(): boolean {
    return (
      this.vesselIds.dirty ||
      this.vesselOperator.dirty ||
      this.shippingAgent.dirty ||
      this.portCallId.dirty ||
      this.vesselServiceId.dirty ||
      this.portRotation.dirty ||
      this.vesselNotes.dirty ||
      this.voyages.value.some(
        voyage =>
          voyage.inboundNumber.dirty || voyage.outboundNumber.dirty || voyage.shippingLine.dirty,
      ) ||
      this.initialValues.voyages?.map(v => v.id) !== this.voyages.value.map(v => v.id)
    )
  }

  get selectedVesselNames(): string {
    return this.selectedVessel?.name ?? ''
  }

  validate = () => {
    return validateSubForm(this)
  }

  clearVesselData() {
    this.vesselIds.setValue([])
    this.setSelectedVessel(undefined)
    this.vesselOperator.setValue(undefined)
    this.shippingAgent.setValue(undefined)
    this.vesselServiceId.setValue(null)
    this.portRotation.setValue(undefined)
    this.vesselNotes.setValue('')
  }

  reset() {
    this.setData(this.initialValues)
    this.setSelectedVessel(this.initialValues.vessel)
  }

  // todo: test
  updateInitialValueWithCurrentData() {
    this.initialValues = this.data
    this.vesselIds.updateInitialValue(this.vesselIds.value)
    this.vesselOperator.updateInitialValue(this.vesselOperator.value)
    this.shippingAgent.updateInitialValue(this.shippingAgent.value)
    this.portCallId.updateInitialValue(this.portCallId.value)
    this.vesselServiceId.updateInitialValue(this.vesselServiceId.value)
    this.portRotation.updateInitialValue(this.portRotation.value)
    this.vesselNotes.updateInitialValue(this.vesselNotes.value)
    this.voyages.value.forEach(v => {
      v.inboundNumber.updateInitialValue(v.inboundNumber.value)
      v.outboundNumber.updateInitialValue(v.outboundNumber.value)
      v.shippingLine.updateInitialValue(v.shippingLine.value)
    })
  }
}
